import { ChatMessage, ChatMessageUnion } from '@/types';
import { cn } from '@/utils/twMerge';
import { motion } from 'framer-motion';
import Markdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { memo, useMemo } from 'react';
import {
  MessageContact,
  MessageFiles,
  MessageImage,
  MessageImagesSeries,
  MessagePrice,
  MessageProductCard,
  MessageProductCardSeries,
  MessageProductCardVelio,
  MessageproductCardVelioSeries,
  MessageUserShowMore,
} from '.';
import { supportedFileTypes } from '../icons/FileIcon';
import MessageFeedback from './MessageFeedback';
import { FileComponent } from './MessageFiles';
type MessageProps = {
  message: ChatMessage;
};

const baseMessageContentStyle = cn(
  'text-md leading-md max-w-[87.5%] bg-transparent text-wrap break-words formatted-text'
);

// Move remarkPlugins outside to prevent recreation on each render
const remarkPlugins = [remarkGfm];

const customComponents: Components = {
  a(props) {
    const { href, children } = props;

    // TODO: Check if the href is ending with a supported file extension
    const isFile = supportedFileTypes.some(type => href?.endsWith(type));

    if (isFile) {
      const name = children?.toString() || href || 'File name';
      const type = href?.split('.').pop() || 'pdf';
      const url = href || '';

      return (
        <span className="my-[10px] inline-block w-full">
          <FileComponent file={{ url, metadata: { name, type } }} position="first" isSingleFile />
        </span>
      );
    }

    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

// Memoized Markdown component to prevent unnecessary re-renders
const MarkdownContent = memo(
  ({ content, messageId }: { content: string; messageId: string }) => {
    return (
      <div key={messageId} className={cn(baseMessageContentStyle)}>
        <Markdown components={customComponents} remarkPlugins={remarkPlugins}>
          {content}
        </Markdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if content or messageId changes
    return prevProps.content === nextProps.content && prevProps.messageId === nextProps.messageId;
  }
);

MarkdownContent.displayName = 'MarkdownContent';

const renderMessageContent = (message: ChatMessageUnion, messageId: string) => {
  switch (message.type) {
    case 'text':
      return (
        <MarkdownContent
          key={messageId}
          content={message.content as string}
          messageId={messageId}
        />
      );
    case 'image':
      return <MessageImage key={messageId} message={message} messageId={messageId} />;
    case 'imageSeries':
      return <MessageImagesSeries key={messageId} message={message} messageId={messageId} />;
    case 'files':
      return <MessageFiles key={messageId} message={message} />;
    case 'contact':
      return <MessageContact key={messageId} message={message} messageId={messageId} />;
    case 'productCard':
      return <MessageProductCard key={messageId} message={message} messageId={messageId} />;
    case 'productCardSeries':
      return <MessageProductCardSeries key={messageId} message={message} messageId={messageId} />;
    case 'productCardVelio':
      return <MessageProductCardVelio key={messageId} message={message} messageId={messageId} />;
    case 'productCardVelioSeries':
      return (
        <MessageproductCardVelioSeries key={messageId} message={message} messageId={messageId} />
      );
    case 'price':
      return <MessagePrice key={messageId} message={message} messageId={messageId} />;
    case 'show-more':
      return <MessageUserShowMore key={messageId} message={message} messageId={messageId} />;
    case 'reactNode':
      return (
        <div key={messageId} className={cn(baseMessageContentStyle)}>
          {message.content}
        </div>
      );
    default:
      return (
        <div key={messageId} className={cn(baseMessageContentStyle)}>
          Sorry, we don't support this message type yet.
        </div>
      );
  }
};

const Message = ({ message }: MessageProps) => {
  // Memoize messageContent to prevent recreation on each render
  // Must be called before early returns to follow React Hooks rules
  const messageContent = useMemo(() => {
    if (!message.output) {
      return [];
    }

    //@ts-ignore
    const output = message.output.messageContent || message.output;

    return output.map((outputItem: ChatMessageUnion, idx: number) => {
      const id = `${message.id}-${idx}`;

      // if (outputItem.type === 'text') {
      //   // Replace single newlines with double newlines without touching existing multi-newline sequences
      //   outputItem.content = (outputItem.content as string).replace(
      //     /(^|[^\n])\n(?!\n)/g,
      //     (_m, p1) => `${p1}\n\n`
      //   );
      // }

      return {
        key: `chat-message-${id}`,
        content: renderMessageContent(outputItem, id),
      };
    });
  }, [message.output, message.id]);

  if (!message.output) {
    return null;
  }

  //@ts-ignore
  if (message.output.messageContent) {
    //@ts-ignore
    message.output = message.output.messageContent;
  }

  const { role } = message.output[0];

  const isUser = role === 'user';
  const isBot = role === 'assistant';
  const isSystem = role === 'system';

  return (
    <motion.div
      layout
      key={message.id}
      id={message.id}
      initial={{ opacity: 0, x: isUser ? 20 : -100 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        ease: 'anticipate',
        duration: 0.3,
      }}
      className={cn(
        'flex w-full max-w-[780px] flex-col gap-[8px] text-base',
        isUser ? 'chat-message-user items-end' : 'chat-message-bot items-start'
      )}
    >
      {isUser &&
        message.output[0].type === 'show-more' &&
        messageContent.map(({ content }: { content: React.ReactNode }) => content)}
      {isUser && message.output[0].type !== 'show-more' && (
        <div
          className={cn(
            'message-content text-md leading-md max-w-[87.5%] text-wrap break-words rounded-xl bg-widget-gray-100 px-[12px] py-[8px]'
          )}
          dangerouslySetInnerHTML={{
            __html: (message?.output?.[0]?.content as string)?.replaceAll('\n', '<br />') as string,
          }}
        />
      )}
      {(isBot || isSystem) &&
        messageContent.map(({ content }: { content: React.ReactNode }) => content)}
      {isBot && <MessageFeedback messageId={message.id} />}
    </motion.div>
  );
};

export default memo(Message);
